import React, { useState, useEffect, useCallback, useRef } from 'react';
import { tokens as tk } from '../theme';
import MyTimecard from './MyTimecard';

const API = 'https://tcio-api.onrender.com';
const auth = () => ({ Authorization: 'Bearer ' + localStorage.getItem('tcio_token') });
const DOT = '\u00B7', DASH = '\u2014', L = '\u2039', R = '\u203a', X = '\u2715', ARR = '\u2192';
const card = { background: tk.surface, border: '1px solid ' + tk.border, borderRadius: tk.radiusCard, boxShadow: '0 2px 8px rgba(34,48,62,0.06)' };
const lab = { fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: tk.textSecondary, fontWeight: 600 };
const DOW = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'];
const dOnly = (v) => String(v || '').slice(0, 10);
const addDays = (ds, n) => { const d = new Date(dOnly(ds) + 'T00:00:00'); d.setDate(d.getDate() + n); return d.toISOString().slice(0, 10); };
const fmtDate = (ds) => new Date(dOnly(ds) + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const fmtLong = (ds) => new Date(dOnly(ds) + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' });
const fmtDow = (ds) => new Date(dOnly(ds) + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'long' });
const fmtRange = (m) => new Date(dOnly(m) + 'T00:00:00').toLocaleDateString('en-US', { month: 'long', day: 'numeric' }) + ' ' + DASH + ' ' + new Date(dOnly(addDays(m, 6)) + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric' }) + ', ' + dOnly(m).slice(0, 4);
const fmtTime = (t) => { if (!t) return ''; const [h, mm] = String(t).split(':').map(Number);
  const ap = h >= 12 ? 'PM' : 'AM'; const hh = h % 12 === 0 ? 12 : h % 12;
  return hh + ':' + String(mm).padStart(2, '0') + ' ' + ap; };
const fmtClock = (iso) => iso ? new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : '';
const fmtDur = (ms) => { if (ms == null || ms < 0) return DASH;
  const m = Math.floor(ms / 60000); return Math.floor(m / 60) + 'h ' + String(m % 60).padStart(2, '0') + 'm'; };
const hrs1 = (n) => (Math.round(n * 10) / 10).toFixed(1);
const thisMonday = () => { const d = new Date(); const dow = (d.getDay() + 6) % 7; d.setDate(d.getDate() - dow); return d.toISOString().slice(0, 10); };
const STATUS_LABEL = { scheduled: 'Scheduled', coverage: 'Coverage', worked: 'Worked', on_call: 'On call', out: 'Called out', no_show: 'No-show' };

const shiftEnd = (s) => {
  const rolls = String(s.end_time) < String(s.start_time);
  return new Date(dOnly(rolls ? addDays(s.work_date, 1) : s.work_date) + 'T' + String(s.end_time));
};
const LIVE_GRACE_MS = 4 * 3600000;
const isLive = (s, nowMs) => s.clock_state === 'clocked_in' && nowMs <= shiftEnd(s).getTime() + LIVE_GRACE_MS;
const isIncomplete = (s, nowMs) => s.clock_state === 'clocked_in' && !isLive(s, nowMs);
const workedMs = (s, nowMs) => {
  let total = 0, openIn = null;
  (s.punches || []).forEach(p => {
    if (p.type === 'clock_in') openIn = new Date(p.at).getTime();
    else if (p.type === 'clock_out' && openIn != null) { total += new Date(p.at).getTime() - openIn; openIn = null; }
  });
  if (openIn != null && isLive(s, nowMs)) total += Math.max(0, nowMs - openIn);
  return total;
};
const lastIn = (s) => { const a = (s.punches || []).filter(p => p.type === 'clock_in'); return a.length ? a[a.length - 1].at : null; };
const lastOut = (s) => { const a = (s.punches || []).filter(p => p.type === 'clock_out'); return a.length ? a[a.length - 1].at : null; };

function useIsMobile() {
  const [m, setM] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  useEffect(() => { const h = () => setM(window.innerWidth < 768);
    window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, []);
  return m;
}
const getLoc = () => new Promise((resolve) => {
  if (!navigator.geolocation) return resolve(null);
  const to = setTimeout(() => resolve(null), 8000);
  navigator.geolocation.getCurrentPosition(
    (p) => { clearTimeout(to); resolve({ lat: p.coords.latitude, lng: p.coords.longitude, accuracy: p.coords.accuracy }); },
    () => { clearTimeout(to); resolve(null); },
    { enableHighAccuracy: true, timeout: 7500, maximumAge: 30000 });
});

// TCIO outline icon set (lucide-style), primary color tk.ai
const I = {
  cal: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M8 3v4M16 3v4M3 10h18"/></svg>),
  clock: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v5l3 2"/></svg>),
  glance: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><path d="M3 17l5-6 4 4 6-8 3 4"/></svg>),
  bolt: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinejoin="round"><path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z"/></svg>),
  activity: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><path d="M3 12h4l3-8 4 16 3-8h4"/></svg>),
  help: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M9.5 9a2.5 2.5 0 1 1 3.4 2.3c-.8.4-1.4 1-1.4 1.9V14"/><circle cx="12" cy="17" r="0.5" fill={c}/></svg>),
  fix: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="9"/><path d="M12 8v4l2.5 1.5"/><path d="M16.5 16.5l2 2"/></svg>),
  warn: (c, s) => (<svg width={s || 15} height={s || 15} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3L2 20h20L12 3z"/><path d="M12 10v4"/><circle cx="12" cy="17" r="0.5" fill={c}/></svg>),
  chat: (c, s) => (<svg width={s || 17} height={s || 17} viewBox="0 0 24 24" fill="none" stroke={c} strokeWidth="2" strokeLinecap="round"><path d="M21 12a8 8 0 0 1-8 8H4l2-3a8 8 0 1 1 15-5z"/></svg>),
};
const SectionHead = ({ icon, text, right }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{icon}<span style={lab}>{text}</span></span>
    {right || null}
  </div>);

export default function MyShifts({ user }) {
  const isMobile = useIsMobile();
  const [monday, setMonday] = useState(thisMonday);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState('');
  const [punchBusy, setPunchBusy] = useState(null);
  const [punchErr, setPunchErr] = useState('');
  const [punchNote, setPunchNote] = useState(null);
  const [detail, setDetail] = useState(null);
  const [fixFor, setFixFor] = useState(null);
  const [fixKind, setFixKind] = useState('missed_out');
  const [fixTime, setFixTime] = useState('');
  const [fixReason, setFixReason] = useState('');
  const [fixBusy, setFixBusy] = useState(false);
  const [fixErr, setFixErr] = useState('');
  const [fixSent, setFixSent] = useState('');
  const [nowMs, setNowMs] = useState(Date.now());
  const [hovAct, setHovAct] = useState(null);
  const [hovRow, setHovRow] = useState(null);
  const busyRef = useRef(false);
  const today = new Date().toISOString().slice(0, 10);

  const load = useCallback(async () => {
    setLoadErr('');
    try {
      const r = await fetch(API + '/api/scheduler/my-shifts?week_of=' + monday, { headers: auth() });
      const j = await r.json();
      if (j.ok) setData(j); else setLoadErr(j.error || 'load failed');
    } catch (e) { setLoadErr('network'); }
    setLoading(false);
  }, [monday]);
  useEffect(() => { setLoading(true); load(); }, [load]);
  useEffect(() => { const t = setInterval(() => setNowMs(Date.now()), 30000); return () => clearInterval(t); }, []);
  useEffect(() => {
    const wake = () => { if (!document.hidden) { setNowMs(Date.now()); load(); } };
    window.addEventListener('focus', wake); document.addEventListener('visibilitychange', wake);
    return () => { window.removeEventListener('focus', wake); document.removeEventListener('visibilitychange', wake); };
  }, [load]);

  const punch = async (shift, kind) => {
    if (busyRef.current) return;
    busyRef.current = true; setPunchBusy(shift.id); setPunchErr(''); setPunchNote(null);
    const location = await getLoc();
    try {
      const r = await fetch(API + '/api/scheduler/assignments/' + shift.id + '/clock', {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind, location }) });
      const j = await r.json();
      if (j.ok) { setPunchNote({ kind, at: j.at, outside_fence: j.outside_fence, distance_m: j.distance_m, off_schedule: j.off_schedule, delta: j.schedule_delta_min, no_location: !location }); await load(); }
      else setPunchErr(j.error || 'punch failed');
    } catch (e) { setPunchErr('network error'); }
    busyRef.current = false; setPunchBusy(null); setNowMs(Date.now());
  };

  const openFix = (shift, kind) => {
    if (!shift) return;
    setFixFor(shift); setFixKind(kind || 'missed_out'); setFixErr(''); setFixSent('');
    setFixTime(dOnly(shift.work_date) + 'T' + String(shift.end_time).slice(0, 5));
    setFixReason('');
  };
  const submitFix = async () => {
    if (!fixFor || fixBusy) return;
    setFixBusy(true); setFixErr('');
    try {
      const r = await fetch(API + '/api/scheduler/assignments/' + fixFor.id + '/correction', {
        method: 'POST', headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ kind: fixKind, proposed_time: new Date(fixTime).toISOString(), reason: fixReason }) });
      const j = await r.json();
      if (j.ok) { setFixSent('Sent. A supervisor will review and apply the fix.'); await load(); }
      else setFixErr(j.error || 'request failed');
    } catch (e) { setFixErr('network error'); }
    setFixBusy(false);
  };

  const shifts = (data && data.shifts) || [];
  const week = (data && data.week) || null;
  const todays = shifts.filter(s => s.work_date === today);
  const primary = todays.find(s => isLive(s, nowMs)) || todays[0] || null;
  const upcoming = shifts.filter(s => s.work_date > today && !['out', 'no_show'].includes(s.status)).sort((a, b) => a.work_date < b.work_date ? -1 : 1)[0] || null;
  const attention = shifts.filter(s => isIncomplete(s, nowMs) && !s.correction_pending);
  const incompleteCount = shifts.filter(s => isIncomplete(s, nowMs)).length;
  const weekSched = shifts.filter(s => !['out', 'no_show'].includes(s.status)).reduce((a, s) => a + Number(s.paid_hours || 0), 0);
  const weekRec = shifts.reduce((a, s) => a + workedMs(s, nowMs), 0) / 3600000;
  const outside = weekRec - weekSched;
  const star = incompleteCount > 0 ? '*' : '';
  const allPunches = shifts.flatMap(s => (s.punches || []).map(p => ({ ...p, work_date: s.work_date, shift: s })))
    .sort((a, b) => new Date(b.at) - new Date(a.at)).slice(0, 6);

  const canIn = (s) => s.clock_state !== 'clocked_in' && ['scheduled', 'coverage', 'on_call'].includes(s.status);
  const canOut = (s) => s.clock_state === 'clocked_in';
  const punchBtn = { font: 'inherit', fontWeight: 600, fontSize: isMobile ? 17 : 16, minHeight: isMobile ? 56 : 52,
    width: '100%', borderRadius: tk.radiusControl, cursor: 'pointer', border: '1px solid ' + tk.ai, background: tk.ai, color: tk.surface };
  const navBtn = { font: 'inherit', fontSize: 13, fontWeight: 600, minWidth: 36, minHeight: 34, borderRadius: tk.radiusControl,
    cursor: 'pointer', border: '1px solid ' + tk.border, background: tk.surface, color: tk.ink };
  const ghostBtn = { ...navBtn, fontSize: 12.5, padding: '5px 12px' };
  const inp = { font: 'inherit', fontSize: 13, padding: '8px 10px', borderRadius: tk.radiusControl, border: '1px solid ' + tk.border, color: tk.ink, background: tk.surface, width: '100%', boxSizing: 'border-box' };
  const chip = (txt, color, bg) => (
    <span style={{ fontSize: 11.5, fontWeight: 600, color, background: bg, border: '1px solid ' + color, borderRadius: 999, padding: '3px 10px' }}>{txt}</span>);
  const glanceRow = (k, v, color) => (
    <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '11px 0', borderBottom: '1px solid ' + tk.borderSoft, fontSize: 13 }}>
      <span style={{ color: tk.textSecondary }}>{k}</span>
      <span style={{ color: color || tk.ink, fontWeight: 600, fontSize: 13.5, fontVariantNumeric: 'tabular-nums', textAlign: 'right' }}>{v}</span>
    </div>);
  const actionRow = (icon, txt, onClick, idx) => (
    <button key={txt} onClick={onClick} onMouseEnter={() => setHovAct(idx)} onMouseLeave={() => setHovAct(null)}
      style={{ font: 'inherit', display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 50,
        padding: '0 14px', marginBottom: 8, borderRadius: tk.radiusControl, border: '1px solid ' + tk.border,
        background: hovAct === idx ? tk.aiSoft : tk.surface,
        color: tk.ink, fontSize: 13.5, fontWeight: 600, cursor: 'pointer', textAlign: 'left' }}>
      {icon}<span style={{ flex: 1 }}>{txt}</span><span style={{ color: tk.textMuted }}>{ARR}</span>
    </button>);

  const todayBody = () => {
    if (loading) return <div style={{ height: 210, borderRadius: tk.radiusControl, background: tk.surfaceSoft }} aria-label="Loading" />;
    if (loadErr) return <div style={{ fontSize: 13.5, color: tk.danger, padding: '30px 0' }}>Schedule unavailable. <button style={{ ...navBtn, marginLeft: 8 }} onClick={() => { setLoading(true); load(); }}>Retry</button></div>;
    if (!primary) return (
      <div>
        <div style={{ fontSize: 22, fontWeight: 700, color: tk.ink, marginTop: 6 }}>{"You're off today."}</div>
        <div style={{ margin: '16px 0', padding: '24px 16px', borderRadius: tk.radiusControl, background: tk.canvas,
          border: '1px dashed ' + tk.border, textAlign: 'center' }}>
          {I.cal(tk.ai, 22)}
          <div style={{ fontSize: 14, fontWeight: 600, color: tk.textSecondary, marginTop: 6 }}>No shift scheduled</div>
        </div>
        <div style={{ ...lab, marginBottom: 4 }}>Next shift</div>
        {upcoming
          ? <div style={{ fontSize: 15, color: tk.ink }}>
              <span style={{ fontWeight: 700 }}>{fmtDow(upcoming.work_date)} {DOT} {fmtTime(upcoming.start_time)} {DASH} {fmtTime(upcoming.end_time)}</span>
              <span style={{ display: 'block', fontSize: 13, color: tk.textSecondary, marginTop: 2 }}>{upcoming.house_name || 'Facility-wide'}</span>
            </div>
          : <div style={{ fontSize: 13.5, color: tk.textSecondary }}>No more shifts scheduled this week.</div>}
        <button style={{ ...ghostBtn, marginTop: 14, color: tk.surface, background: tk.ai, borderColor: tk.ai, fontWeight: 600 }} onClick={() => setMonday(addDays(monday, 7))}>View next week {ARR}</button>
      </div>);
    const s = primary;
    const live = isLive(s, nowMs);
    const done = s.clock_state === 'clocked_out';
    const wMs = workedMs(s, nowMs);
    return (
      <div>
        <div style={{ ...lab, marginBottom: 2 }}>Your shift</div>
        <div style={{ fontSize: isMobile ? 26 : 32, fontWeight: 700, color: tk.ink, letterSpacing: '-0.6px', fontVariantNumeric: 'tabular-nums' }}>
          {fmtTime(s.start_time)} {DASH} {fmtTime(s.end_time)}</div>
        <div style={{ fontSize: 13.5, color: tk.textSecondary, marginTop: 4 }}>
          {hrs1(Number(s.paid_hours || 0))} hours {DOT} {s.house_name || 'Facility-wide'}
          {s.correction_pending ? <span style={{ marginLeft: 8 }}>{chip('Correction pending', tk.warning, tk.warningBg)}</span> : null}</div>
        {live &&
          <div role="status" aria-label={'On the clock, worked ' + fmtDur(wMs)}
            style={{ margin: '16px 0 0', padding: '15px 18px', borderRadius: tk.radiusControl, background: tk.successBg, border: '1px solid ' + tk.success,
              display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: tk.success, letterSpacing: '0.5px' }}>{'\u25CF'} ON THE CLOCK</span>
            <span style={{ textAlign: 'right' }}>
              <span style={{ fontSize: 26, fontWeight: 700, color: tk.ink, fontVariantNumeric: 'tabular-nums' }}>{fmtDur(wMs)}</span>
              <span style={{ display: 'block', fontSize: 11.5, color: tk.textSecondary }}>Worked so far</span></span>
          </div>}
        {done &&
          <div style={{ margin: '16px 0 0', padding: '12px 16px', borderRadius: tk.radiusControl, background: tk.canvas, border: '1px solid ' + tk.border, fontSize: 13.5 }}>
            <span style={{ fontWeight: 600, color: tk.ink }}>Shift completed.</span>
            <span style={{ color: tk.textSecondary }}> Actual {fmtClock(lastIn(s))} {DASH} {fmtClock(lastOut(s))} {DOT} Worked {fmtDur(wMs)}</span>
            {!s.correction_pending && <button style={{ ...ghostBtn, marginLeft: 10 }} onClick={() => openFix(s, 'wrong_time')}>Fix my time</button>}
          </div>}
        {isIncomplete(s, nowMs) &&
          <div style={{ margin: '16px 0 0', padding: '12px 16px', borderRadius: tk.radiusControl, background: tk.warningBg, border: '1px solid ' + tk.warning, fontSize: 13.5,
            display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            {I.warn(tk.warning)}<span style={{ fontWeight: 600, color: tk.warning }}>Incomplete time entry.</span>
            <span style={{ color: tk.ink }}>Clock-in at {fmtClock(lastIn(s))} with no clock-out.</span>
            {!s.correction_pending && <button style={{ ...ghostBtn, color: tk.warning, borderColor: tk.warning }} onClick={() => openFix(s, 'missed_out')}>Fix my time</button>}
          </div>}
        {(canIn(s) || live) &&
          <div style={{ marginTop: 16 }}>
            <button style={{ ...punchBtn, opacity: punchBusy === s.id ? 0.6 : 1 }} disabled={punchBusy === s.id}
              aria-label={live ? 'Clock out of your ' + fmtTime(s.start_time) + ' shift' : 'Clock in to your ' + fmtTime(s.start_time) + ' shift'}
              onClick={() => punch(s, live ? 'out' : 'in')}>
              {punchBusy === s.id ? 'Working\u2026' : live ? 'Clock Out' : 'Clock In'}</button>
            <div style={{ fontSize: 12.5, color: tk.textSecondary, marginTop: 9 }}>
              {lastIn(s) ? 'Clocked in at ' + fmtClock(lastIn(s)) + ' ' + DOT + ' ' : ''}Scheduled to clock out at {fmtTime(s.end_time)}</div>
          </div>}
        {punchErr && <div style={{ marginTop: 8, fontSize: 12.5, color: tk.danger }}>{punchErr}</div>}
        {punchNote &&
          <div style={{ marginTop: 8, fontSize: 12.5, color: (punchNote.outside_fence || punchNote.off_schedule) ? tk.warning : tk.textSecondary }}>
            {punchNote.kind === 'in' ? 'Clocked in' : 'Clocked out'} at {fmtClock(punchNote.at)}.
            {punchNote.off_schedule ? ' ' + Math.abs(punchNote.delta) + ' min ' + (punchNote.delta < 0 ? 'before' : 'after') + ' schedule ' + DASH + ' noted for supervisor review.' : ''}
            {punchNote.outside_fence ? ' Location ' + punchNote.distance_m + ' m from the house ' + DASH + ' flagged for review.' : ''}
            {punchNote.no_location ? ' No location shared.' : ''}
          </div>}
        {todays.length > 1 && <div style={{ marginTop: 10, fontSize: 12, color: tk.textSecondary }}>{todays.length} shifts today {DASH} see This week below.</div>}
      </div>);
  };

  const todayChip = primary
    ? (isIncomplete(primary, nowMs) ? chip('Needs attention', tk.warning, tk.warningBg)
      : isLive(primary, nowMs) ? chip((primary.punches || []).some(p => p.off_schedule) ? 'Off schedule' : 'On schedule',
          (primary.punches || []).some(p => p.off_schedule) ? tk.warning : tk.success,
          (primary.punches || []).some(p => p.off_schedule) ? tk.warningBg : tk.successBg)
      : primary.clock_state === 'clocked_out' ? chip('Completed', tk.success, tk.successBg) : null)
    : null;

  const glanceCard = () => {
    if (primary) {
      const w = workedMs(primary, nowMs) / 3600000; const sch = Number(primary.paid_hours || 0);
      return <div>
        <SectionHead icon={I.glance(tk.ai)} text="Today at a glance" />
        {glanceRow('Scheduled hours', hrs1(sch) + ' hr')}
        {glanceRow('Worked so far', hrs1(w) + ' hr')}
        {glanceRow(w > sch ? 'Over scheduled time' : 'Remaining', hrs1(Math.abs(sch - w)) + ' hr', w > sch ? tk.warning : undefined)}
        {glanceRow('Next', canOut(primary) ? 'Clock out at ' + fmtTime(primary.end_time)
          : canIn(primary) ? 'Clock in for ' + fmtTime(primary.start_time)
          : upcoming ? 'Next shift ' + fmtDate(upcoming.work_date) : 'Nothing scheduled')}
      </div>;
    }
    return <div>
      <SectionHead icon={I.glance(tk.ai)} text="Week at a glance" />
      {glanceRow('Scheduled', hrs1(weekSched) + ' hr')}
      {glanceRow('Recorded' + star, hrs1(weekRec) + ' hr')}
      {glanceRow('Outside schedule' + star, (outside >= 0 ? '+' : '') + hrs1(outside) + ' hr', Math.abs(outside) > 0.05 ? tk.warning : undefined)}
      {glanceRow('Entries needing attention', String(incompleteCount), incompleteCount ? tk.warning : undefined)}
      {glanceRow('Next shift', upcoming ? fmtDow(upcoming.work_date) + ' ' + fmtTime(upcoming.start_time) : 'None this week')}
      {incompleteCount > 0 && <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 8 }}>*{incompleteCount} incomplete time {incompleteCount > 1 ? 'entries' : 'entry'} may affect totals.</div>}
    </div>;
  };

  const dayCell = (i) => {
    const day = addDays(monday, i);
    const ds = shifts.filter(s => s.work_date === day);
    const isToday = day === today;
    const needsAttn = ds.some(s => isIncomplete(s, nowMs));
    const off = !ds.length;
    return (
      <div key={day} onClick={() => ds.length && setDetail({ day, shifts: ds })}
        role={ds.length ? 'button' : undefined} tabIndex={ds.length ? 0 : undefined}
        onKeyDown={e => { if (e.key === 'Enter' && ds.length) setDetail({ day, shifts: ds }); }}
        style={{ background: isToday ? tk.aiSoft : off ? tk.surfaceSoft : tk.surface,
          border: isToday ? '2px solid ' + tk.ai : '1px solid ' + tk.border,
          borderLeft: needsAttn && !isToday ? '3px solid ' + tk.warning : undefined,
          borderRadius: tk.radiusControl, padding: isToday ? 11 : 12, minHeight: isMobile ? 0 : 130, cursor: ds.length ? 'pointer' : 'default' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
          <span style={{ fontSize: 11.5, fontWeight: 600, color: isToday ? tk.ai : off ? tk.textMuted : tk.textSecondary, letterSpacing: '0.5px' }}>{DOW[i]}</span>
          <span style={{ fontSize: 11.5, color: tk.textMuted }}>{fmtDate(day)}</span>
        </div>
        {isToday && <div style={{ fontSize: 10, fontWeight: 700, color: tk.ai, letterSpacing: '0.1em', marginTop: 1 }}>TODAY</div>}
        {off && <div style={{ fontSize: 12.5, color: tk.textMuted, marginTop: 22, letterSpacing: '1.5px', textAlign: 'center' }}>OFF</div>}
        {ds.slice(0, 2).map(s => (
          <div key={s.id} style={{ marginTop: 10, opacity: ['out', 'no_show'].includes(s.status) ? 0.55 : 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: tk.ink, fontVariantNumeric: 'tabular-nums', lineHeight: 1.25 }}>
              {fmtTime(s.start_time)} {DASH}<br />{fmtTime(s.end_time)}</div>
            <div style={{ fontSize: 12, color: tk.textSecondary, marginTop: 4 }}>{hrs1(Number(s.paid_hours || 0))} hr</div>
            {s.house_name && <div style={{ fontSize: 12, color: tk.textSecondary }}>{s.house_name}</div>}
            <div style={{ fontSize: 11.5, marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
              {isIncomplete(s, nowMs) ? <span style={{ color: tk.warning, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>{I.warn(tk.warning, 12)} Needs attention</span>
                : isLive(s, nowMs) ? <span style={{ color: tk.success, fontWeight: 600 }}>{'\u25CF'} On the clock</span>
                : s.clock_state === 'clocked_out' ? <span style={{ color: tk.textSecondary }}>Completed</span>
                : ['out', 'no_show'].includes(s.status) ? <span style={{ color: tk.textMuted }}>{STATUS_LABEL[s.status]}</span>
                : <span style={{ color: tk.textMuted }}>Scheduled</span>}
            </div>
          </div>))}
        {ds.length > 2 && <div style={{ fontSize: 11.5, color: tk.ai, marginTop: 6, fontWeight: 600 }}>{ds.length} shifts</div>}
      </div>);
  };

  const totals = [
    ['Scheduled', hrs1(weekSched), tk.ink],
    ['Recorded' + star, hrs1(weekRec), tk.ink],
    ['Outside schedule' + star, (outside >= 0 ? '+' : '') + hrs1(outside), Math.abs(outside) > 0.05 ? tk.warning : tk.ink],
  ];
  if (week && Number(week.overtime) > 0) totals.push(['Overtime (CA)', hrs1(Number(week.overtime)), tk.warning]);
  if (week && Number(week.doubletime) > 0) totals.push(['Doubletime (CA)', hrs1(Number(week.doubletime)), tk.danger]);

  const punchStatus = (p) => {
    if (isIncomplete(p.shift, nowMs) && p.type === 'clock_in' && !lastOut(p.shift))
      return <span style={{ color: tk.warning, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 4 }}>{I.warn(tk.warning, 12)} Needs clock-out</span>;
    if (isLive(p.shift, nowMs)) return <span style={{ color: tk.success, fontWeight: 600 }}>{'\u25CF'} Current shift</span>;
    return <span style={{ color: tk.textSecondary }}>Completed</span>;
  };

  return (
    <div style={{ background: tk.canvas, minHeight: '100%', width: '100%' }}>
    <div style={{ width: 'calc(100% - ' + (isMobile ? 20 : 56) + 'px)', maxWidth: 1450, margin: '0 auto', padding: isMobile ? '10px 0 24px' : '20px 0 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 24, fontWeight: 700, color: tk.ink, letterSpacing: '-0.4px' }}>My Workday</div>
          <div style={{ fontSize: 13, color: tk.textSecondary }}>Your schedule, time clock, and work summary</div>
        </div>
        <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
          <button style={navBtn} aria-label="Previous week" onClick={() => setMonday(addDays(monday, -7))}>{L}</button>
          <span style={{ fontSize: 13.5, fontWeight: 600, color: tk.ink }}>{fmtRange(monday)}</span>
          <button style={navBtn} aria-label="Next week" onClick={() => setMonday(addDays(monday, 7))}>{R}</button>
          {monday !== thisMonday() && <button style={{ ...navBtn, color: tk.ai, borderColor: tk.ai }} onClick={() => setMonday(thisMonday())}>Today</button>}
        </div>
      </div>

      {attention.length > 0 &&
        <div style={{ ...card, borderColor: tk.warning, background: tk.warningBg, padding: '12px 16px', marginBottom: 16,
          display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 13.5, display: 'flex', alignItems: 'center', gap: 8 }}>{I.warn(tk.warning)}
            <span><span style={{ fontWeight: 600, color: tk.warning }}>Time entry needs attention</span>
            <span style={{ color: tk.ink }}> {DASH} {attention.map(s => fmtDate(s.work_date)).join(', ')} {attention.length > 1 ? 'have' : 'has'} a clock-in with no clock-out.</span></span></span>
          <button style={{ ...ghostBtn, borderColor: tk.warning, color: tk.warning, fontWeight: 600, background: tk.surface }} onClick={() => openFix(attention[0], 'missed_out')}>Fix My Time {ARR}</button>
        </div>}

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '48fr 27fr 25fr', gap: 16, marginBottom: 16 }}>
        <div style={{ ...card, padding: isMobile ? 16 : 22 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{I.cal(tk.ai)}<span style={lab}>Today</span>
              <span style={{ fontSize: 12.5, color: tk.textMuted, marginLeft: 4 }}>{fmtLong(today)}</span></span>
            {todayChip}
          </div>
          {todayBody()}
        </div>
        <div style={{ ...card, padding: isMobile ? 16 : 20 }}>{glanceCard()}</div>
        <div style={{ ...card, padding: isMobile ? 16 : 20 }}>
          <SectionHead icon={I.bolt(tk.ai)} text="Quick actions" />
          {actionRow(I.fix(tk.ai), 'Fix My Time', () => openFix(attention[0] || shifts.filter(s => (s.punches || []).length).sort((a, b) => b.work_date < a.work_date ? -1 : 1)[0] || primary, attention.length ? 'missed_out' : 'wrong_time'), 0)}
          {actionRow(I.cal(tk.ai), 'View next week', () => setMonday(addDays(monday, 7)), 1)}
          <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 4 }}>Timecards, time off, and availability arrive with the HR records build.</div>
        </div>
      </div>

      <div style={{ ...card, padding: isMobile ? 14 : 20, marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: 10, marginBottom: 12 }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{I.cal(tk.ai)}<span style={lab}>This week</span></span>
          <div style={{ display: 'flex', gap: 26, flexWrap: 'wrap' }}>
            {totals.map(([k, v, c]) => (
              <span key={k} style={{ textAlign: 'right' }}>
                <span style={{ fontSize: 22, fontWeight: 700, color: c, fontVariantNumeric: 'tabular-nums' }}>{v}</span>
                <span style={{ display: 'block', fontSize: 11.5, color: tk.textSecondary }}>{k}</span></span>))}
          </div>
        </div>
        {incompleteCount > 0 && <div style={{ fontSize: 11.5, color: tk.textMuted, marginBottom: 10 }}>*{incompleteCount} incomplete time {incompleteCount > 1 ? 'entries' : 'entry'} may affect these totals.</div>}
        {loading ? <div style={{ height: 130, borderRadius: tk.radiusControl, background: tk.surfaceSoft }} />
          : loadErr ? <div style={{ fontSize: 13, color: tk.danger }}>Schedule unavailable.</div>
          : !shifts.length ? <div style={{ fontSize: 13.5, color: tk.textSecondary, padding: '6px 0' }}>{"You don't have any scheduled shifts this week."}</div>
          : <div style={{ display: isMobile ? 'flex' : 'grid', flexDirection: 'column', gridTemplateColumns: 'repeat(7, 1fr)', gap: isMobile ? 8 : 9 }}>
              {(isMobile ? [0, 1, 2, 3, 4, 5, 6].filter(i => shifts.some(s => s.work_date === addDays(monday, i)) || addDays(monday, i) === today) : [0, 1, 2, 3, 4, 5, 6]).map(dayCell)}
            </div>}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '60fr 40fr', gap: 16 }}>
        <div style={{ ...card, padding: isMobile ? 14 : 20 }}>
          <SectionHead icon={I.activity(tk.ai)} text="Recent time activity" />
          {!allPunches.length ? <div style={{ fontSize: 13, color: tk.textSecondary }}>No time activity yet this week.</div>
            : <div>
                {!isMobile &&
                  <div style={{ display: 'grid', gridTemplateColumns: '120px 90px 90px 1fr 160px', gap: 8, padding: '4px 0 8px',
                    borderBottom: '1px solid ' + tk.border }}>
                    {['DATE', 'TIME', 'TYPE', 'SHIFT', 'STATUS'].map(h => <span key={h} style={lab}>{h}</span>)}
                  </div>}
                {allPunches.map((p, i) => (
                  <div key={i} onMouseEnter={() => setHovRow(i)} onMouseLeave={() => setHovRow(null)}
                    style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '120px 90px 90px 1fr 160px', gap: 8,
                    padding: '10px 6px', margin: '0 -6px', borderRadius: 6, background: hovRow === i ? tk.surfaceSoft : 'transparent',
                    borderBottom: '1px solid ' + tk.borderSoft, fontSize: 12.5, alignItems: 'baseline' }}>
                    <span style={{ color: tk.ink, fontWeight: 600 }}>{new Date(dOnly(p.work_date) + 'T00:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}</span>
                    <span style={{ color: tk.ink, fontVariantNumeric: 'tabular-nums' }}>{fmtClock(p.at)}</span>
                    <span style={{ color: tk.textSecondary }}>{p.type === 'clock_in' ? 'Clock In' : 'Clock Out'}{p.corrected ? ' (corrected)' : ''}</span>
                    {!isMobile && <span style={{ color: tk.textSecondary, fontVariantNumeric: 'tabular-nums' }}>{fmtTime(p.shift.start_time)} {DASH} {fmtTime(p.shift.end_time)}</span>}
                    <span>{punchStatus(p)}{p.outside_fence ? <span style={{ color: tk.warning }}> {DOT} location</span> : ''}{p.off_schedule ? <span style={{ color: tk.warning }}> {DOT} off schedule</span> : ''}</span>
                  </div>))}
              </div>}
        </div>
        <div style={{ ...card, padding: isMobile ? 14 : 20 }}>
          <SectionHead icon={I.help(tk.ai)} text="Need help?" />
          <div style={{ padding: '14px 16px', borderRadius: tk.radiusControl, background: tk.aiSoft, border: '1px solid ' + tk.border }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{I.chat(tk.ai)}
              <span style={{ fontSize: 13.5, fontWeight: 600, color: tk.ink }}>Report an issue with my time</span></div>
            <div style={{ fontSize: 12.5, color: tk.textSecondary, margin: '6px 0 12px' }}>Missed a punch? Wrong time? Send it to a supervisor {DASH} your original punches always stay on record.</div>
            <button style={{ ...ghostBtn, color: tk.ai, borderColor: tk.ai, fontWeight: 600, background: tk.surface }}
              onClick={() => openFix(attention[0] || shifts.filter(s => (s.punches || []).length)[0] || primary, attention.length ? 'missed_out' : 'wrong_time')}>Fix My Time</button>
          </div>
        </div>
      </div>

      {detail &&
        <div onClick={() => setDetail(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.45)', zIndex: 60,
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: isMobile ? '100%' : 440, maxHeight: '80vh', overflowY: 'auto',
            borderRadius: isMobile ? '14px 14px 0 0' : tk.radiusCard, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 10 }}>
              <span style={{ color: tk.ink, fontSize: 15, fontWeight: 600 }}>{fmtLong(detail.day)}</span>
              <button style={navBtn} onClick={() => setDetail(null)} aria-label="Close">{X}</button>
            </div>
            {detail.shifts.map(s => (
              <div key={s.id} style={{ borderTop: '1px solid ' + tk.borderSoft, padding: '10px 0', fontSize: 13 }}>
                <div style={{ fontWeight: 600, color: tk.ink }}>{fmtTime(s.start_time)} {DASH} {fmtTime(s.end_time)} {DOT} {hrs1(Number(s.paid_hours || 0))} hr</div>
                <div style={{ color: tk.textSecondary, marginTop: 3 }}>{s.house_name || 'Facility-wide'} {DOT} {STATUS_LABEL[s.status] || s.status} {s.correction_pending ? chip('Correction pending', tk.warning, tk.warningBg) : null}</div>
                {(s.punches || []).length > 0 &&
                  <div style={{ color: tk.textSecondary, marginTop: 4 }}>
                    Actual: {(s.punches || []).map(p => (p.type === 'clock_in' ? 'in ' : 'out ') + fmtClock(p.at)).join(' ' + DOT + ' ')}
                    {' ' + DOT + ' '}Worked {fmtDur(workedMs(s, nowMs))}{isIncomplete(s, nowMs) ? <span style={{ color: tk.warning, fontWeight: 600 }}> {DOT} needs clock-out</span> : ''}</div>}
                {(s.punches || []).length > 0 && !s.correction_pending &&
                  <button style={{ ...ghostBtn, marginTop: 6 }} onClick={() => { setDetail(null); openFix(s, isIncomplete(s, nowMs) ? 'missed_out' : 'wrong_time'); }}>Fix my time</button>}
              </div>))}
          </div>
        </div>}

      <MyTimecard user={user} isMobile={isMobile} />
      {fixFor &&
        <div onClick={() => !fixBusy && setFixFor(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(10,34,57,0.45)', zIndex: 70,
          display: 'flex', alignItems: isMobile ? 'flex-end' : 'center', justifyContent: 'center' }}>
          <div onClick={e => e.stopPropagation()} style={{ ...card, width: isMobile ? '100%' : 420,
            borderRadius: isMobile ? '14px 14px 0 0' : tk.radiusCard, padding: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <span style={{ color: tk.ink, fontSize: 15, fontWeight: 600 }}>Fix my time</span>
              <button style={navBtn} onClick={() => setFixFor(null)} aria-label="Close">{X}</button>
            </div>
            <div style={{ fontSize: 12.5, color: tk.textSecondary, marginBottom: 12 }}>
              {fmtLong(fixFor.work_date)} {DOT} {fmtTime(fixFor.start_time)} {DASH} {fmtTime(fixFor.end_time)} {DOT} {fixFor.house_name || 'Facility-wide'}</div>
            {fixSent ? <div style={{ fontSize: 13, color: tk.success, fontWeight: 600 }}>{fixSent}</div>
              : <div>
                  <div style={{ ...lab, marginBottom: 4 }}>What happened</div>
                  <select style={inp} value={fixKind} onChange={e => setFixKind(e.target.value)}>
                    <option value="missed_out">I forgot to clock out</option>
                    <option value="missed_in">I forgot to clock in</option>
                    <option value="wrong_time">A punch has the wrong time</option>
                  </select>
                  <div style={{ ...lab, margin: '10px 0 4px' }}>{fixKind === 'missed_in' ? 'When I actually started' : fixKind === 'missed_out' ? 'When I actually left' : 'The correct time'}</div>
                  <input style={inp} type="datetime-local" value={fixTime} onChange={e => setFixTime(e.target.value)} />
                  <div style={{ ...lab, margin: '10px 0 4px' }}>Reason</div>
                  <input style={inp} placeholder="e.g. left at 10:30 but phone died" value={fixReason} onChange={e => setFixReason(e.target.value)} />
                  {fixErr && <div style={{ fontSize: 12.5, color: tk.danger, marginTop: 8 }}>{fixErr}</div>}
                  <button style={{ ...punchBtn, marginTop: 14, opacity: fixBusy ? 0.6 : 1 }} disabled={fixBusy} onClick={submitFix}>
                    {fixBusy ? 'Sending\u2026' : 'Send to supervisor'}</button>
                  <div style={{ fontSize: 11.5, color: tk.textMuted, marginTop: 8 }}>A supervisor reviews and applies the fix. Your original punches stay on record.</div>
                </div>}
          </div>
        </div>}
    </div>
    </div>
  );
}
